import type {ClientFunctionsAdapter} from "@buildwithflux/firebase-functions-adapter";
import {
    ApplyAutoLayoutJobRequestBodySchema,
    AutoLayoutApi,
    AutoLayoutApiVersion,
    ConfirmAutoLayoutJobRequestBodySchema,
    CreateAutoLayoutJobRequest,
    CreateAutoLayoutJobResult,
    DiscardAutoLayoutJobRequestBodySchema,
    PauseAutoLayoutJobRequestBodySchema,
    ResumeAutoLayoutJobRequestBodySchema,
    SubmitAutoLayoutJobRequestBodySchema,
    UserUid,
    type NetId,
    type NetType,
    type OrganizationUid,
} from "@buildwithflux/models";
import {Logger} from "@buildwithflux/shared";
import axios, {type AxiosInstance} from "axios";

import type {FluxAuthClaimsService} from "modules/auth/services/FluxAuthClaimsService";
import {UseFeatureFlagsStore} from "modules/feature_flags/FeatureFlagsStore";
import {selectAutoLayoutVersion} from "modules/feature_flags/hooks";

import {
    type ApplyJobData,
    type AutoLayoutApiClient,
    type CancelJobData,
    type ClassifyNetsData,
    type ConfirmJobData,
    type CreateJobResult,
    type PauseJobData,
    type PopulateJobData,
    type ResumeJobData,
    type SubmitJobData,
} from "./AutoLayoutApiClient";
import {type AutoLayoutEventBus} from "./AutoLayoutEventBus";
import {DeepPcbAutoLayoutBaseService} from "./DeepPcbAutoLayoutBaseService";
import {type DeepPcbAutoLayoutEventMap} from "./DeepPcbAutoLayoutEventBus";

/**
 * DeepPCB implementation of auto-layout API communication.
 * Extracted from the monolithic DeepPcbAutoLayoutService.
 */
export class DeepPcbAutoLayoutApiClient extends DeepPcbAutoLayoutBaseService implements AutoLayoutApiClient {
    private readonly apiVersion: AutoLayoutApiVersion;
    private readonly axiosClient: AxiosInstance;

    constructor(
        eventBus: AutoLayoutEventBus<DeepPcbAutoLayoutEventMap>,
        logger: Logger,
        private readonly functionsAdapter: ClientFunctionsAdapter,
        private readonly fluxAuthClaimsService: FluxAuthClaimsService,
        private readonly useFeatureFlagsStore: UseFeatureFlagsStore,
    ) {
        super("AutoLayoutApiClient", eventBus, logger);
        this.apiVersion = selectAutoLayoutVersion(this.useFeatureFlagsStore.getState());
        this.axiosClient = this.createAxiosClient();
    }

    public async createJob(
        projectUid: string,
        userUid: string,
        organizationUid?: OrganizationUid,
    ): Promise<CreateJobResult> {
        const url = AutoLayoutApi.urlFor(AutoLayoutApi.action.create, this.apiVersion);

        // Extend the base schema to include userUid, matching the backend's CreateAutoLayoutJobRequestWithUserUid
        const CreateAutoLayoutJobRequestWithUserUid = CreateAutoLayoutJobRequest.extend({
            userUid: UserUid,
        });

        const requestBody = CreateAutoLayoutJobRequestWithUserUid.parse({
            projectUid,
            userUid,
            organizationUid,
            allocationStyle: "cold",
        });

        const response = await this.axiosClient.post<CreateAutoLayoutJobResult>(url, requestBody);
        const result = CreateAutoLayoutJobResult.parse(response.data);

        if (result.type === "failure") {
            throw new Error(`Failed to create auto-layout job: ${result.reason}`);
        }

        return {
            uploadUrl: result.uploadUrl,
            jobUid: result.jobUid,
        };
    }

    public async populateJob(data: PopulateJobData): Promise<void> {
        const {boardData, uploadUrl} = data;

        // Upload board data to GCS - this step populates the job with board data
        try {
            // TODO: Need to check with Nick to see what config we need to do in order for cdn2.flux.ai domain to work
            const uploadUrl_hack = uploadUrl.replace("cdn2.flux.ai", "storage.googleapis.com");

            await axios.put(uploadUrl_hack, boardData, {
                headers: {
                    // Need to unset Content-Type to make it work...
                    "Content-Type": "",
                },
            });

            this.logger.debug("Board data uploaded successfully");
        } catch (error) {
            this.logger.error("Failed to upload board data", {error});
            throw new Error("Failed to upload board data to cloud storage");
        }

        this.logger.info("Auto-layout job populated", {jobUid: data.jobUid});
    }

    public async submitJob(data: SubmitJobData): Promise<void> {
        const {jobUid, userUid, inputBoardLocation, projectPath} = data;

        const url = AutoLayoutApi.urlFor(AutoLayoutApi.action.submit, this.apiVersion);

        const requestBody = SubmitAutoLayoutJobRequestBodySchema.parse({
            userUid,
            jobUid,
            inputBoardLocation,
            ...(projectPath && {projectPath}),
        });

        await this.axiosClient.post(url, requestBody);

        this.logger.info("Auto-layout job submitted", {jobUid});
        this.emit("job.submit.requested", {jobUid});
    }

    public async confirmJob(data: ConfirmJobData): Promise<void> {
        const {jobUid, userUid, organizationUid} = data;

        const url = AutoLayoutApi.urlFor(AutoLayoutApi.action.confirm, this.apiVersion);

        const requestBody = ConfirmAutoLayoutJobRequestBodySchema.parse({
            userUid,
            jobUid,
            organizationUid,
        });

        await this.axiosClient.post(url, requestBody);

        this.logger.info("Auto-layout job confirmed", {jobUid});
        this.emit("job.confirm.requested", {jobUid});
    }

    public async pauseJob(data: PauseJobData): Promise<void> {
        const {jobUid, userUid, organizationUid} = data;

        const url = AutoLayoutApi.urlFor(AutoLayoutApi.action.pause, this.apiVersion);

        const requestBody = PauseAutoLayoutJobRequestBodySchema.parse({
            userUid,
            jobUid,
            organizationUid,
        });

        await this.axiosClient.post(url, requestBody);

        this.logger.info("Auto-layout job pause requested", {jobUid});
        this.emit("job.pause.requested", {jobUid});
    }

    public async resumeJob(data: ResumeJobData): Promise<void> {
        const {jobUid, userUid, organizationUid} = data;

        const url = AutoLayoutApi.urlFor(AutoLayoutApi.action.resume, this.apiVersion);

        const requestBody = ResumeAutoLayoutJobRequestBodySchema.parse({
            userUid,
            jobUid,
            organizationUid,
        });

        await this.axiosClient.post(url, requestBody);

        this.logger.info("Auto-layout job resume requested", {jobUid});
        this.emit("job.resume.requested", {jobUid});
    }

    public async cancelJob(data: CancelJobData): Promise<void> {
        const {jobUid, userUid, organizationUid} = data;

        const url = AutoLayoutApi.urlFor(AutoLayoutApi.action.discard, this.apiVersion);

        const requestBody = DiscardAutoLayoutJobRequestBodySchema.parse({
            userUid,
            jobUid,
            organizationUid,
        });

        await this.axiosClient.post(url, requestBody);

        this.logger.info("Auto-layout job cancel requested", {jobUid});
        this.emit("job.cancel.requested", {jobUid});
    }

    public async applyJob(data: ApplyJobData): Promise<void> {
        const {jobUid, userUid, organizationUid, iterationIndex} = data;

        const url = AutoLayoutApi.urlFor(AutoLayoutApi.action.apply, this.apiVersion);

        const requestBody = ApplyAutoLayoutJobRequestBodySchema.parse({
            userUid,
            jobUid,
            organizationUid,
            ...(iterationIndex !== undefined && {iterationIndex}),
        });

        await this.axiosClient.post(url, requestBody);

        this.logger.info("Auto-layout job apply requested", {jobUid});
        this.emit("job.apply.requested", {jobUid});
    }

    public async classifyNets(data: ClassifyNetsData): Promise<Record<NetId, NetType>> {
        const response = await this.functionsAdapter.classifyNets({
            documentUid: data.documentUid,
            requestMetaData: data.requestMetaData,
        });

        this.logger.info("Net classification completed");

        return response.data;
    }

    /**
     * Creates a configured axios client with authentication and default headers
     */
    private createAxiosClient(): AxiosInstance {
        const client = axios.create({
            headers: {
                "Content-Type": "application/json",
            },
        });

        // Add auth token to every request
        client.interceptors.request.use((config) => {
            const token = this.fluxAuthClaimsService.getFluxToken();
            if (!token) {
                throw new Error("No authentication token available");
            }
            config.headers.Authorization = `Bearer ${token}`;
            return config;
        });

        return client;
    }
}
